import { InvestmentAccount } from '@/hooks/useInvestments';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { RiskRatingBadge } from '@/components/risk-rating-badge/RiskRatingBadge';

interface InvestmentCardProps {
  investment: InvestmentAccount & { convertedBalance?: number };
  currency: string;
  onEdit: () => void;
  onDelete: () => void;
  onClick?: () => void;
}

import { useNavigate } from 'react-router-dom';

/**
 * InvestmentCard component displays a single investment account with details
 * and action buttons for editing and deleting.
 * 
 * @param investment - The investment account to display
 * @param currency - The selected currency for display
 * @param onEdit - Callback function when edit button is clicked
 * @param onDelete - Callback function when delete button is clicked
 * @param onClick - Optional callback function when card is clicked (alternative to default navigation)
 * 
 * @example
 * <InvestmentCard
 *   investment={investment}
 *   currency="USD"
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 * />
 */
export function InvestmentCard({ investment, currency, onEdit, onDelete, onClick }: InvestmentCardProps) {
  const navigate = useNavigate();

  const formatCurrency = (amount: number, curr: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
    }).format(amount);
  };

  const formatInterestRate = (rate: number) => {
    return `${rate.toFixed(2)}%`;
  };

  // Use converted balance if available and currency is different, otherwise use original balance
  const displayBalance = investment.convertedBalance !== undefined && investment.currency !== currency
    ? investment.convertedBalance
    : investment.balance;
  
  const displayCurrency = investment.convertedBalance !== undefined && investment.currency !== currency
    ? currency
    : investment.currency;

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent click event from bubbling to parent elements
    e.stopPropagation();
    
    // If custom onClick handler is provided, use it
    if (onClick) {
      onClick();
    } else {
      // Default behavior: navigate to investment detail view
      navigate(`/investments/${investment.id}`);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <Card 
      className="w-full cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e as any);
        }
      }}
      aria-label={`View ${investment.fund_name} investment details. Balance: ${formatCurrency(displayBalance, displayCurrency)}. Interest rate: ${formatInterestRate(investment.interest_rate)}`}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-lg font-semibold">
            {investment.fund_name}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{investment.fund_manager}</p>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <RiskRatingBadge ratingName={investment.risk_rating?.name} />
          {investment.currency !== currency && (
            <Badge variant="outline">
              Original: {investment.currency}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Account Number:</span>
            <span className="font-medium">{investment.account_number}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Current Balance:</span>
            <span className="font-medium">
              {formatCurrency(displayBalance, displayCurrency)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Interest Rate:</span>
            <span className="font-medium">
              {formatInterestRate(investment.interest_rate)}
            </span>
          </div>
        </div>
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEditClick}
            className="flex-1"
            aria-label={`Edit ${investment.fund_name}`}
          >
            <Edit className="h-4 w-4 mr-2" aria-hidden="true" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeleteClick}
            className="flex-1 text-red-600 hover:text-red-700"
            aria-label={`Delete ${investment.fund_name}`}
          >
            <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
